import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material";

const levels = ["Beginner", "Intermediate", "Advanced", "Expert"];

const AddSkillsDialog = ({
  open,
  handleClose,
  handleSave,
  skillsList = {},
  isLoading,
  selectedSkillsList,
}) => {
  const [selectedSkills, setSelectedSkills] = useState({});

  // ✅ Keep selectedSkills synced with prop
  useEffect(() => {
    if (selectedSkillsList && selectedSkillsList.length > 0) {
      // Flatten array of objects into one object
      const merged = Object.assign({}, ...selectedSkillsList);
      setSelectedSkills(merged);
    } else {
      setSelectedSkills({});
    }
  }, [selectedSkillsList]);

  const getLevelName = (value) => levels[value - 1] || "Beginner";

  // ✅ Toggle main skill (do not auto-select sub-skills)
  const handleToggleSkill = (skill) => {
    setSelectedSkills((prev) => {
      const updated = { ...prev };
      if (updated[skill]) {
        // Deselect main skill → remove entry
        delete updated[skill];
      } else {
        // Select main skill → initialize with empty sub-skills
        updated[skill] = {
          level: "Intermediate",
          subSkills: [],
        };
      }
      return updated;
    });
  };

  // ✅ Handle level change - store capitalized
  const handleLevelChange = (skill, value) => {
    const levelName = getLevelName(value);
    setSelectedSkills((prev) => ({
      ...prev,
      [skill]: { ...prev[skill], level: levelName },
    }));
  };

  // ✅ Allow manual toggle for sub-skills
  const handleSubSkillToggle = (skill, sub) => {
    setSelectedSkills((prev) => {
      const currentSubs = prev[skill]?.subSkills || [];
      const isSelected = currentSubs.some((s) => s.name === sub);

      const updatedSubs = isSelected
        ? currentSubs.filter((s) => s.name !== sub)
        : [...currentSubs, { name: sub, level: "Intermediate" }];

      return {
        ...prev,
        [skill]: { ...prev[skill], subSkills: updatedSubs },
      };
    });
  };

  // ✅ Handle sub-skill level change
  const handleSubSkillLevelChange = (skill, subName, value) => {
    const levelName = getLevelName(value);
    setSelectedSkills((prev) => {
      const updatedSubs = (prev[skill]?.subSkills || []).map((s) =>
        s.name === subName ? { ...s, level: levelName } : s,
      );
      return {
        ...prev,
        [skill]: { ...prev[skill], subSkills: updatedSubs },
      };
    });
  };

  // ✅ Format and save
  const handleDialogSave = () => {
    const formatted = Object.entries(selectedSkills).map(([skill, data]) => ({
      [skill]: data,
    }));
    handleSave(formatted);
    handleClose();
  };

  // ✅ Sort main skills alphabetically
  const sortedSkills = Object.keys(skillsList).sort((a, b) =>
    a.localeCompare(b),
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      // PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "#6c5ce7",
          color: "white",
        }}
      >
        Add Technical Skills
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          backgroundColor: "#fafafa",
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 8,
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress sx={{ color: "#6c5ce7" }} />
          </Box>
        ) : (
          <>
            {sortedSkills.map((skill, index) => {
              const selected = !!selectedSkills[skill];
              const skillData = selectedSkills[skill.trim()] || {};
              const levelIndex = levels.findIndex(
                (l) => l.toLowerCase() === skillData.level?.toLowerCase(),
              );
              const sliderValue = levelIndex !== -1 ? levelIndex + 1 : 2;

              // ✅ Sort sub-skills alphabetically
              const sortedSubSkills = [...(skillsList[skill] || [])].sort(
                (a, b) => a.localeCompare(b),
              );

              return (
                <Paper
                  key={index}
                  sx={{
                    p: 5,
                    mb: 2,
                    ml: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    backgroundColor: selected ? "#fff" : "#f5f5f5",
                    boxShadow: selected ? "0 2px 6px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected}
                          onChange={() => handleToggleSkill(skill)}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "1rem",
                          }}
                        >
                          {skill}
                        </Typography>
                      }
                    />
                  </Stack>

                  {selected && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography sx={{ mb: 1, fontWeight: 500 }}>
                        Skill Level
                      </Typography>
                      <Slider
                        value={sliderValue}
                        min={1}
                        max={4}
                        step={1}
                        marks={levels.map((l, i) => ({
                          value: i + 1,
                          label: l,
                        }))}
                        onChange={(e, val) => handleLevelChange(skill, val)}
                        sx={{
                          "& .MuiSlider-markLabel": { fontSize: "0.8rem" },
                          width: "80%",
                        }}
                      />

                      {sortedSubSkills.length > 0 && (
                        <>
                          <Typography sx={{ mt: 2, fontWeight: 500 }}>
                            Sub-skills
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            {sortedSubSkills.map((sub, i) => {
                              const subSkillData = (
                                skillData.subSkills || []
                              ).find((s) => s.name === sub);
                              const subSelected = !!subSkillData;
                              const subLevelIndex = levels.findIndex(
                                (l) =>
                                  l.toLowerCase() ===
                                  subSkillData?.level?.toLowerCase(),
                              );
                              const subSliderValue =
                                subLevelIndex !== -1 ? subLevelIndex + 1 : 2;

                              return (
                                <Box key={i} sx={{ mb: 2, ml: 1 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={subSelected}
                                        onChange={() =>
                                          handleSubSkillToggle(skill, sub)
                                        }
                                      />
                                    }
                                    label={
                                      <Typography sx={{ fontSize: "0.9rem" }}>
                                        {sub}
                                      </Typography>
                                    }
                                    sx={{ m: 0 }}
                                  />
                                  {subSelected && (
                                    <Box sx={{ ml: 4, mt: 1 }}>
                                      <Slider
                                        value={subSliderValue}
                                        min={1}
                                        max={4}
                                        step={1}
                                        marks={levels.map((l, i) => ({
                                          value: i + 1,
                                          label: l,
                                        }))}
                                        onChange={(e, val) =>
                                          handleSubSkillLevelChange(
                                            skill,
                                            sub,
                                            val,
                                          )
                                        }
                                        sx={{
                                          "& .MuiSlider-markLabel": {
                                            fontSize: "0.7rem",
                                          },
                                          width: "60%",
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Paper>
              );
            })}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleDialogSave}
          variant="contained"
          sx={{
            textTransform: "none",
            px: 3,
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSkillsDialog;
